import {
    getFirestore,
    doc,
    getDoc,
  } from "firebase/firestore";
import { Result } from "../../models/result";
import { User } from "../../models/user/user";
import httpRepo from "../httpRepo";
import { agentEndpoints } from "../apiConfig";

const db = getFirestore();

/**
 * Fetches a user by their document ID from Firestore
 * Used for silent profile refresh without requiring re-authentication
 *
 * @param {string} schoolId - The ID of the school
 * @param {string} userId - The document ID of the user
 * @returns {Promise<Result>} A Promise that resolves to a Result object with user data
 */
export const getUserById = async (schoolId, userId) => {
  if (!schoolId || !userId) {
    return new Result({
      message: "School ID and user ID must be provided.",
      code: "INVALID_INPUT",
    });
  }

  try {
    const userDocRef = doc(db, "schools", schoolId, "users", userId);
    const userDoc = await getDoc(userDocRef);

    if (userDoc.exists()) {
      const userData = userDoc.data();
      userData.userId = userDoc.id; // Include the document ID in the user data
      const user = User.fromMap(userData, schoolId);
      
      return new Result({
        value: user,
        message: "User profile fetched successfully."
      });
    } else {
      return new Result({
        message: "User not found.",
        code: "USER_NOT_FOUND"
      });
    }
  } catch (error) {
    console.error("Error fetching user by ID from Firestore:", error);
    return new Result({
      value: null,
      message: `An error occurred while fetching user profile: ${error.message}`,
      code: "FIRESTORE_ERROR",
    });
  }
};

/**
 * Logs a staff member in through the ai-service (POST /v1/login).
 * The ai-service checks the password and returns a Firebase custom token,
 * which useUserStore.loginUser passes to useAuthStore.signInWithCustomToken.
 *
 * @param {Object} params
 * @param {string} params.schoolId - The ID of the school
 * @param {string} params.username - The username
 * @param {string} params.password - The password
 * @returns {Promise<Result>} Result with { customToken, userId }
 */
export const loginWithPassword = async ({ schoolId, username, password }) => {
  try {
    if (!schoolId || !username || !password) {
      return new Result({
        message: "School ID, username, and password must be provided.",
        code: "INVALID_INPUT",
      });
    }

    const requestBody = {
      schoolId: schoolId,
      username: username,
      password: password,
    };
    // Public API: there is no signed-in user yet, so no Authorization header
    const response = await httpRepo.post(agentEndpoints.login, requestBody, { isPublicAPI: true });

    if (!response.success) {
      return new Result({
        message: response.message || "Invalid username or password.",
        code: response.code,
      });
    }

    const loginValue = response.value;
    if (!loginValue.token || !loginValue.user || !loginValue.user.userId) {
      return new Result({
        message: "Login failed. Please try again.",
        code: "INVALID_LOGIN_RESPONSE",
      });
    }

    return new Result({
      value: {
        customToken: loginValue.token,
        userId: loginValue.user.userId,
      },
    });
  } catch (error) {
    console.error("Error logging in through the ai-service:", error);
    return new Result({
      message: `Login failed: ${error.message}`,
      code: "LOGIN_ERROR",
    });
  }
};
